/* ===========================================================================
   GO: Homebanner Slideshow - companion to the theme's slideshow-component.js
   Author: bieler.digital
   The slider itself (fade, autoplay, animated bullets) is the theme's own
   <slideshow-component>. This file only adds what that component lacks:
     1. --go-hb-offset: distance from document top to the section (announcement
        bar + header + anything above) so "Bildschirmfüllend" can be
        calc(100svh - offset). Re-measured on resize / header height changes.
     2. Bullet colour follows the per-slide text-colour override
        (data-go-controls-rgb on the active .swiper-slide).
     3. Theme Editor: selecting a slide block slides to it and pauses autoplay.
     4. Cross-fade duration: slideshow-component.js hard-codes Swiper's default
        speed (300 ms); data-go-speed (setting "Überblenddauer") overrides
        swiper.params.speed after init - autoplay, bullets and slideTo all
        read that value at transition time.
   theme.js and slideshow-component.js stay untouched.
   =========================================================================== */
(function () {
  if (customElements.get('go-homebanner-slideshow')) return;

  class GoHomebannerSlideshow extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.slideshow = this.querySelector('slideshow-component');
      this.controls = this.querySelector('.swiper-controls');

      this.measureOffset = this.measureOffset.bind(this);
      this.measureOffset();
      window.addEventListener('resize', this.measureOffset, { passive: true });
      window.addEventListener('load', this.measureOffset, { once: true });
      // header.js sets --header-height in a rAF after load; re-measure then.
      requestAnimationFrame(() => requestAnimationFrame(this.measureOffset));

      this.whenSliderReady((swiper) => {
        const speed = parseInt(this.dataset.goSpeed, 10);
        if (speed > 0) swiper.params.speed = speed;

        this.syncControlsColor(swiper);
        swiper.on('realIndexChange', () => this.syncControlsColor(swiper));

        if (window.Shopify && Shopify.designMode) {
          this.section.addEventListener('shopify:block:select', (event) => {
            const slide = event.target.closest('.swiper-slide');
            if (!slide) return;
            const index = Number(slide.dataset.index);
            if (Number.isNaN(index)) return;
            if (swiper.params.loop) swiper.slideToLoop(index);
            else swiper.slideTo(index);
            swiper.autoplay && swiper.autoplay.stop();
          });
          this.section.addEventListener('shopify:block:deselect', () => {
            swiper.autoplay && swiper.params.autoplay && swiper.autoplay.start();
          });
        }
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.measureOffset);
    }

    // slideshow-component initialises Swiper in its constructor, but the
    // element may upgrade after us (both scripts are deferred) -> poll briefly.
    whenSliderReady(callback, tries = 0) {
      const instance = this.slideshow && this.slideshow.sliderInstance;
      if (instance && instance.slider) {
        callback(instance.slider);
        return;
      }
      if (tries > 50) return;
      setTimeout(() => this.whenSliderReady(callback, tries + 1), 60);
    }

    measureOffset() {
      const top = Math.max(0, Math.round(this.getBoundingClientRect().top + window.scrollY));
      // Only trust the measurement while the section is near the top of the
      // document (it is the homebanner). Further down, viewport mode should
      // simply mean "one screen high" -> offset 0.
      const offset = top < window.innerHeight ? top : 0;
      this.style.setProperty('--go-hb-offset', offset + 'px');
    }

    syncControlsColor(swiper) {
      if (!this.controls) return;
      const active = swiper.slides[swiper.activeIndex];
      const rgb = active && active.dataset.goControlsRgb;
      if (rgb) this.controls.style.setProperty('--go-hb-controls-rgb', rgb);
      else this.controls.style.removeProperty('--go-hb-controls-rgb');
    }
  }

  customElements.define('go-homebanner-slideshow', GoHomebannerSlideshow);
})();
